import { XIcon } from "lucide-react";
import { format } from "date-fns";
import useSWRMutation from "swr/mutation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Chip } from "@/components/badge/chip";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { mutateRequest, putFetcherKeyed } from "@/lib/swr";
import { refreshInbox } from "@/hooks/inbox";
import type { Prd } from "@/types/domain";

// Due date editor (WED-143): a date picker that PUTs `dueAt` (local midnight
// as ISO) or clears it. Mostly for todos — the Inbox sorts overdue ones first.
const toInputValue = (iso?: string | null) =>
  iso ? format(new Date(iso), "yyyy-MM-dd") : "";

export const DueEditor = ({
  prd,
  readOnly,
}: {
  prd: Prd;
  readOnly?: boolean;
}) => {
  const { toast } = useToast();
  const { trigger, isMutating } = useSWRMutation(
    `/prds/${prd.id}#due`,
    putFetcherKeyed,
    {
      onSuccess: () => {
        mutateRequest(`/prds/${prd.id}`);
        mutateRequest("/prds");
        refreshInbox();
      },
      onError: (e: unknown) => toastError(toast, "设置截止时间失败", e, "提交失败"),
    }
  );
  const overdue =
    !!prd.dueAt && !prd.myCompletedAt && new Date(prd.dueAt) < new Date();
  if (readOnly) {
    return prd.dueAt ? (
      <Chip tone={overdue ? "red" : "neutral"}>{toInputValue(prd.dueAt)}</Chip>
    ) : (
      <span className="text-muted-foreground">—</span>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <Input
        type="date"
        value={toInputValue(prd.dueAt)}
        onChange={(e) => {
          const v = e.target.value;
          if (v) trigger({ dueAt: new Date(`${v}T00:00:00`).toISOString() });
        }}
        aria-label="Due date"
        disabled={isMutating}
        className="h-7 w-36 text-xs"
      />
      {overdue && <Chip tone="red">overdue</Chip>}
      {prd.dueAt && (
        <Button
          variant="ghost"
          size="xs"
          aria-label="Clear due date"
          title="Clear due date"
          disabled={isMutating}
          onClick={() => trigger({ dueAt: null })}
          className="h-5 w-5 p-0 text-muted-foreground"
        >
          <XIcon className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
};
